import { useState } from 'react'
import riskEducation from '../../../shared/data/riskEducation.json'

function RiskEducationPanel() {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(null)

  const { appetiteVsTolerance, domains } = riskEducation

  return (
    <div className="mb-6">
      {/* Collapsed Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/30 dark:to-blue-900/30 rounded-lg border border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-800 rounded-lg">
            <svg className="w-5 h-5 text-primary-600 dark:text-accent-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900 dark:text-white">Understanding Risk Appetite vs Risk Tolerance</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Learn the difference and how they work together</p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 space-y-6">
          {/* Main Comparison */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Risk Appetite */}
            <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-800 rounded">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h4 className="font-bold text-blue-900 dark:text-blue-100">Risk Appetite</h4>
                <span className="text-xs px-2 py-0.5 bg-blue-200 dark:bg-blue-700 text-blue-800 dark:text-blue-200 rounded">Strategic</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {appetiteVsTolerance.riskAppetite.definition}
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Set by:</span>
                  <span className="text-gray-800 dark:text-gray-200">{appetiteVsTolerance.riskAppetite.setBy}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Timeframe:</span>
                  <span className="text-gray-800 dark:text-gray-200">{appetiteVsTolerance.riskAppetite.timeframe}</span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/50 rounded text-sm">
                <span className="font-medium text-blue-800 dark:text-blue-200">Example: </span>
                <span className="text-blue-700 dark:text-blue-300 italic">{appetiteVsTolerance.riskAppetite.example}</span>
              </div>
            </div>

            {/* Risk Tolerance */}
            <div className="p-5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center space-x-2 mb-3">
                <div className="p-1.5 bg-green-100 dark:bg-green-800 rounded">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h4 className="font-bold text-green-900 dark:text-green-100">Risk Tolerance</h4>
                <span className="text-xs px-2 py-0.5 bg-green-200 dark:bg-green-700 text-green-800 dark:text-green-200 rounded">Operational</span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                {appetiteVsTolerance.riskTolerance.definition}
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Set by:</span>
                  <span className="text-gray-800 dark:text-gray-200">{appetiteVsTolerance.riskTolerance.setBy}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 dark:text-gray-400">Timeframe:</span>
                  <span className="text-gray-800 dark:text-gray-200">{appetiteVsTolerance.riskTolerance.timeframe}</span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-green-100 dark:bg-green-800/50 rounded text-sm">
                <span className="font-medium text-green-800 dark:text-green-200">Example: </span>
                <span className="text-green-700 dark:text-green-300 italic">{appetiteVsTolerance.riskTolerance.example}</span>
              </div>
            </div>
          </div>

          {/* Relationship */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">How They Work Together</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{appetiteVsTolerance.relationship.summary}</p>
                <div className="mt-3 flex items-center space-x-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="text-blue-600 dark:text-blue-400">Appetite</span>
                    <span className="text-gray-400">=</span>
                    <span className="text-gray-600 dark:text-gray-400">{appetiteVsTolerance.relationship.analogy.appetite}</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
                  <div className="flex items-center space-x-2">
                    <span className="text-green-600 dark:text-green-400">Tolerance</span>
                    <span className="text-gray-400">=</span>
                    <span className="text-gray-600 dark:text-gray-400">{appetiteVsTolerance.relationship.analogy.tolerance}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Domain Explanations */}
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Domain Reference
            </h4>
            <div className="flex flex-wrap gap-2 mb-3">
              {Object.keys(domains).map(domain => (
                <button
                  key={domain}
                  onClick={() => setSelectedDomain(selectedDomain === domain ? null : domain)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedDomain === domain
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {domain}
                </button>
              ))}
            </div>

            {selectedDomain && domains[selectedDomain] && (
              <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="font-semibold text-gray-900 dark:text-white mb-2">{selectedDomain}</h5>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                  {domains[selectedDomain].definition}
                </p>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium text-gray-600 dark:text-gray-400">Key Question: </span>
                    <span className="text-gray-800 dark:text-gray-200">{domains[selectedDomain].appetiteQuestion}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-gray-600 dark:text-gray-400">Tolerance Examples:</span>
                    <ul className="mt-1 space-y-1">
                      {domains[selectedDomain].toleranceExamples.map((example, idx) => (
                        <li key={idx} className="text-gray-700 dark:text-gray-300 flex items-start">
                          <span className="text-primary-500 mr-2">-</span>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Note */}
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Your answers below define your organization's <em>Risk Appetite</em>.
                After the assessment, we'll generate specific <em>Risk Tolerance Thresholds</em> based on your responses.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RiskEducationPanel
